import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Add,
  Edit,
  PersonOff,
  PersonOutline,
  LockReset,
  SecurityUpdateGood,
} from '@mui/icons-material';
import { usersAPI } from '../../services/adminApi';
import { useAuth } from '../../hooks/useAuth';
import ConfirmDialog from '../common/ConfirmDialog';

const emptyForm = {
  username: '',
  email: '',
  first_name: '',
  last_name: '',
  password: '',
  is_staff: false,
  is_active: true,
};

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { user: currentUser } = useAuth();

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  // Password reset
  const [passwordDialog, setPasswordDialog] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  // 2FA reset confirm
  const [reset2faConfirm, setReset2faConfirm] = useState(null);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await usersAPI.list();
      setUsers(res.data.results || res.data);
    } catch (err) {
      setError('Errore nel caricamento utenti.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const openCreate = () => {
    setEditingUser(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setForm({
      username: user.username,
      email: user.email || '',
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      password: '',
      is_staff: user.is_staff,
      is_active: user.is_active,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      const data = { ...form };
      if (!data.password) delete data.password;

      if (editingUser) {
        await usersAPI.update(editingUser.id, data);
        setSuccess('Utente aggiornato.');
      } else {
        if (!data.password) {
          setError('La password è obbligatoria per nuovi utenti.');
          setSaving(false);
          return;
        }
        await usersAPI.create(data);
        setSuccess('Utente creato.');
      }
      setDialogOpen(false);
      loadUsers();
    } catch (err) {
      const detail = err.response?.data;
      if (typeof detail === 'object') {
        const msgs = Object.values(detail).flat().join(' ');
        setError(msgs);
      } else {
        setError('Errore nel salvataggio.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (user) => {
    try {
      const res = await usersAPI.toggleActive(user.id);
      setUsers((prev) => prev.map((u) => (u.id === user.id ? res.data : u)));
      setSuccess(`Utente ${res.data.is_active ? 'attivato' : 'disattivato'}.`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore.');
    }
  };

  const handleResetPassword = async () => {
    if (!passwordDialog) return;
    try {
      await usersAPI.resetPassword(passwordDialog.id, newPassword);
      setPasswordDialog(null);
      setNewPassword('');
      setSuccess('Password reimpostata.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore nel reset password.');
    }
  };

  const handleReset2fa = async () => {
    if (!reset2faConfirm) return;
    try {
      await usersAPI.reset2fa(reset2faConfirm.id);
      setReset2faConfirm(null);
      setSuccess('2FA rimosso.');
      loadUsers();
    } catch (err) {
      setError('Errore nel reset 2FA.');
    }
  };

  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" fontWeight={600}>
          Utenti ({users.length})
        </Typography>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate} size="small">
          Nuovo utente
        </Button>
      </Box>

      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>{success}</Alert>}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Username</TableCell>
              <TableCell>Nome</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Ruolo</TableCell>
              <TableCell>Stato</TableCell>
              <TableCell>2FA</TableCell>
              <TableCell align="right">Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id}>
                <TableCell sx={{ fontWeight: 600 }}>{u.username}</TableCell>
                <TableCell>{u.full_name || u.first_name || '—'}</TableCell>
                <TableCell>{u.email || '—'}</TableCell>
                <TableCell>
                  <Chip
                    label={u.is_staff ? 'Admin' : 'Utente'}
                    color={u.is_staff ? 'primary' : 'default'}
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={u.is_active ? 'Attivo' : 'Disattivo'}
                    color={u.is_active ? 'success' : 'error'}
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={u.has_2fa ? 'Attivo' : 'No'}
                    color={u.has_2fa ? 'success' : 'warning'}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Modifica">
                    <IconButton size="small" onClick={() => openEdit(u)}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {u.id !== currentUser?.id && (
                    <Tooltip title={u.is_active ? 'Disattiva' : 'Attiva'}>
                      <IconButton size="small" onClick={() => handleToggleActive(u)}>
                        {u.is_active ? <PersonOff fontSize="small" color="warning" /> : <PersonOutline fontSize="small" color="success" />}
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Reset password">
                    <IconButton size="small" onClick={() => setPasswordDialog(u)}>
                      <LockReset fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {u.has_2fa && (
                    <Tooltip title="Reset 2FA">
                      <IconButton size="small" color="warning" onClick={() => setReset2faConfirm(u)}>
                        <SecurityUpdateGood fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit User Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingUser ? 'Modifica utente' : 'Nuovo utente'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label="Username"
            value={form.username}
            onChange={handleChange('username')}
            size="small"
            required
            autoFocus
            disabled={Boolean(editingUser)}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Nome"
              value={form.first_name}
              onChange={handleChange('first_name')}
              size="small"
              sx={{ flex: 1 }}
            />
            <TextField
              label="Cognome"
              value={form.last_name}
              onChange={handleChange('last_name')}
              size="small"
              sx={{ flex: 1 }}
            />
          </Box>
          <TextField
            label="Email"
            type="email"
            value={form.email}
            onChange={handleChange('email')}
            size="small"
          />
          <TextField
            label={editingUser ? 'Nuova password (lascia vuoto per non cambiare)' : 'Password'}
            type="password"
            value={form.password}
            onChange={handleChange('password')}
            size="small"
            required={!editingUser}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControlLabel
              control={<Switch checked={form.is_staff} onChange={handleChange('is_staff')} />}
              label="Amministratore"
            />
            <FormControlLabel
              control={<Switch checked={form.is_active} onChange={handleChange('is_active')} />}
              label="Attivo"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Annulla</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!form.username || saving}
            startIcon={saving ? <CircularProgress size={20} /> : null}
          >
            {editingUser ? 'Salva' : 'Crea'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={Boolean(passwordDialog)} onClose={() => setPasswordDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Reset password per {passwordDialog?.username}</DialogTitle>
        <DialogContent>
          <TextField
            label="Nuova password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            size="small"
            fullWidth
            autoFocus
            sx={{ mt: 1 }}
            helperText="Minimo 8 caratteri"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setPasswordDialog(null); setNewPassword(''); }}>Annulla</Button>
          <Button
            variant="contained"
            onClick={handleResetPassword}
            disabled={newPassword.length < 8}
          >
            Reimposta
          </Button>
        </DialogActions>
      </Dialog>

      {/* 2FA Reset Confirm */}
      <ConfirmDialog
        open={Boolean(reset2faConfirm)}
        title="Reset 2FA"
        message={`Rimuovere la 2FA per ${reset2faConfirm?.username}? Dovrà riconfigurarla al prossimo accesso.`}
        onConfirm={handleReset2fa}
        onCancel={() => setReset2faConfirm(null)}
      />
    </Box>
  );
}
